import { AsyncGridProps, ButtonConfig } from './index';
import { useFetchGridData } from './hooks/useFetchGridData';
import { useGridPopup } from './hooks/useGridPopup';
import useBundleTranslation from 'i18n';
import React, { useEffect, useMemo, useState } from 'react';
import { useGridMassActions } from './hooks/useGridMassActions';
import EmptyGrid from './components/EmptyGrid';
import { Container, Grid, Typography } from '@mui/material';
import TableFilters from './components/TableFilters';
import GridLoading from './GridLoading';
import NoSearchResult from './components/NoSearchResult';
import TableButtons from './components/TableButtons';
import GridPopup from './GridPopup';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { prepareTextDataObject } from 'tools/tools';
import { useSelector } from 'react-redux';
import RequestErrorMessage from 'components/common/error/RequestErrorMessage';
import { useGridColumns } from './hooks/useGridColumns';
import { useGridResize } from './hooks/useGridResize';
import { useGridLifecycle } from './hooks/useGridLifecycle';
import { useGridStickyFooter } from './hooks/useGridStickyFooter';
import { useGridActions } from './hooks/useGridActions';
import { GridBodyRenderer } from './components/GridBodyRenderer';
import MITablePagination from './components/MITablePagination';
import FormElement from 'components/common/form/element/FormElement';
import { prepareFormElementProps } from 'components/common/form/formTools';
import { Breakpoint } from '@mui/system';

const SMART_CONFIG_VERSION = 8;

export default function AsyncGridComponent(props: AsyncGridProps) {
    const {
        error,
        resetFiltersButton,
        filtersList,
        searchField,
        gridData,
        isLoading,
        isError,
        columns,
        filtersValue,
        order,
        orderBy,
        legendConfig,
        pagerOptions,
        totalCount,
        pageIndex,
        gridTitle,
        gridEntityType,
        gridDescription,
        dragAndDrop,
        massActions,
        buttons,
        controllerUrl,
        emptyTitle,
        emptyIcon,
        hideEmpty,
        resetColumns,
        switchColumns,
        widthToggle,
        infoButton,
        gridName,
        visibleFilters,
        multilines,
        autoRowHeight,
        defaultRowHeight,
        handleDropRow,
        setPageIndex,
        onSortColumn,
        handleFilterValueChange,
        resetFilters,
        reloadGridData,
        removeFilterValue,
        saveColumns,
        gridParams,
        noSearchResult,
        rowExtraInfoConfig,
        changeViewConfig,
        gridReorderLoading,
        customLegend,
        dataLoadingDone,
        titleHidden,
    } = useFetchGridData(props.uid, props.settingsUrl, props.relatedFields);

    const {
        popupUrl,
        setPopupUrl,
        popupConfig,
        setPopupConfig,
        popupType,
        setPopupType,
        popupMode,
        setPopupMode,
        popupRedirectAfter,
        setPopupRedirectAfter,
        formPostData,
        setFormPostData,
        popupGetMethod,
        setPopupGetMethod,
        popupComponent,
        setPopupComponent,
        handleHidePopup,
        handleConfirmPopup,
        setPopupComponentProps,
        popupComponentProps,
    } = useGridPopup(props.uid);

    const gridButtons: Array<ButtonConfig> = props.hideButtons ? [] : buttons;

    const bundleTranslation = useBundleTranslation([
        'main',
        'components/common/grid/grid',
        'components/common/form/form',
    ]);
    const t = props.t || bundleTranslation.t;

    const [readyToDraw, setReadyToDraw] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const [fullwidthMode, setFullwidthMode] = useState('fixed');
    const [gridDataChanged, setGridDataChanged] = useState(false);
    const [gridParamsApplied, setGridParamsApplied] = useState(false);
    const [showEditSelector, setShowEditSelector] = useState('');
    const [gridView, setGridView] = useState('default');

    const filtered = useMemo(() => {
        let result = false;
        const filters = Object.values(filtersValue);
        filters.forEach((filter) => {
            if (filter !== '') {
                result = true;
            }
        });

        return result;
    }, [filtersValue]);

    const emptyData = gridData.length === 0 && !filtered && !gridReorderLoading && !isLoading;
    const emptyPageGridData = !props.formGrid && emptyData && !isLoading && !gridReorderLoading;
    const massActionsProps = useGridMassActions(gridData, massActions);
    const isDraggable = dragAndDrop?.enabled ?? false;
    const showCheckboxColumn = massActions.enabled && massActionsProps !== undefined;

    const { boxTableRef, gridWidth, setGridWidth, gridWidthApplied, setGridWidthApplied } = useGridResize({
        readyToDraw,
    });

    const {
        columnsWidth,
        columnsWidthPixels,
        visibleColumns,
        setUserInteracted,
        handleColumnSwitch,
        onColumnResize,
        resetGridSettings,
    } = useGridColumns({
        isDraggable,
        gridWidth,
        columns,
        showCheckboxColumn,
        fullwidthMode,
        gridData,
        saveColumns,
        smartConfigVersion: SMART_CONFIG_VERSION,
        gridView,
        t,
        gridParams,
        gridParamsApplied,
        readyToDraw,
        gridWidthApplied,
        isLoading,
        setFullwidthMode,
        setGridWidthApplied,
        setReadyToDraw,
        setGridView,
        widthToggle,
        setGridParamsApplied,
        reloadGridData,
    });

    useGridLifecycle({
        isLoading,
        columns,
        gridData,
        filtered,
        gridDataChanged,
        setGridDataChanged,
        setReadyToDraw,
        showEditSelector,
        setShowEditSelector,
        reloadTrigger: props.reloadTrigger,
        reloadGridData,
        formGrid: props.formGrid,
        onDataStoreChange: props.onDataStoreChange,
    });

    const { refFooter, isStickyGridButtons } = useGridStickyFooter();

    const { afterDataSave, onChangeView } = useGridActions({
        popupRedirectAfter,
        setGridDataChanged,
        reloadGridData,
        gridName,
        setShowEditSelector,
        massActionsProps,
        setGridView,
        setUserInteracted,
        saveColumns,
        columnsWidth,
        visibleColumns,
        fullwidthMode,
        smartConfigVersion: SMART_CONFIG_VERSION,
    });

    const conciergeWidgetWidth = useSelector((state: any) => state.concierge.width);
    const conciergePanelVisible = useSelector((state: any) => state.concierge.visible);

    const { getFinalHtml: getEmptyTitle } = useMemo(() => prepareTextDataObject(emptyTitle), [emptyTitle]);
    const emptyTitleText = useMemo(() => {
        const entityTranslations = gridEntityType
            ? {
                  entity: t(`entity_title.${gridEntityType}`, { count: 1 }),
                  entities: t(`entity_title.${gridEntityType}`, { count: 5 }),
                  entities_lower: t(`entity_title.${gridEntityType}`, { count: 5 }).toLowerCase(),
              }
            : {
                  entity: '',
                  entities: '',
                  entities_lower: '',
              };

        return getEmptyTitle(t, entityTranslations);
    }, [getEmptyTitle, t, gridEntityType]);

    const onFullwidthChange = (mode: string) => {
        setFullwidthMode(mode);
        setUserInteracted(true);
        if (saveColumns) {
            saveColumns(columnsWidth, visibleColumns, mode, gridView, SMART_CONFIG_VERSION);
        }
    };

    useEffect(() => {
        if (gridWidth === 0 && gridData.length > 0 && JSON.stringify(columnsWidth) === JSON.stringify({})) {
            if (boxTableRef && boxTableRef.current && boxTableRef.current.offsetWidth > 0) {
                setGridWidth(boxTableRef.current.offsetWidth);
            }
        }
    }, [gridWidth, gridData, columnsWidth, boxTableRef, setGridWidth]);

    const gridPopup = (
        <GridPopup
            uid={'grid_popup_' + props.uid}
            popupUrl={popupUrl}
            handleHidePopup={handleHidePopup}
            handleConfirmPopup={handleConfirmPopup}
            popupConfig={popupConfig}
            popupType={popupType}
            popupMode={popupMode}
            reloadGridData={reloadGridData}
            formPostData={formPostData}
            getMethod={popupGetMethod}
            component={popupComponent}
            componentProps={popupComponentProps}
            setCheckedKeys={massActionsProps.setCheckedKeys}
            gridData={gridData}
            afterSave={afterDataSave}
        />
    );

    const userBrandThemeMenuProperties = useSelector((state: any) => state.auth?.userInfo?.brandThemeProperties?.menu);
    const themeEditorMenu = useSelector((state: any) => state.themeEditor.menu);
    const themeMenuSettings = { ...(userBrandThemeMenuProperties ?? {}), ...themeEditorMenu };

    const gridContainerMaxWidth = useMemo(() => {
        const settingValue = props.gridContainerMaxWidth;
        if (settingValue === undefined) return 'lg';
        if (settingValue == 'full') return false;
        if (settingValue) return settingValue as Breakpoint;
        return false;
    }, [props.gridContainerMaxWidth]);

    if (emptyPageGridData) {
        return (
            <>
                {isError ? (
                    <RequestErrorMessage error={error} />
                ) : (
                    <EmptyGrid
                        emptyTitle={emptyTitleText}
                        emptyIcon={emptyIcon}
                        gridEntityType={gridEntityType}
                        createButtons={gridButtons ? gridButtons.filter((config) => config.type === 'create') : []}
                        controllerUrl={controllerUrl}
                        t={t}
                        gridName={gridName}
                        height={props.height}
                        gridPopup={gridPopup}
                        setPopupUrl={setPopupUrl}
                        setPopupConfig={setPopupConfig}
                        setPopupType={setPopupType}
                        setPopupMode={setPopupMode}
                        setPopupRedirectAfter={setPopupRedirectAfter}
                        setFormPostData={setFormPostData}
                        setPopupGetMethod={setPopupGetMethod}
                        setPopupComponent={setPopupComponent}
                        setPopupComponentProps={setPopupComponentProps}
                        form={props.form}
                    />
                )}
            </>
        );
    }

    const gridContainerXs = !(fullwidthMode === 'full' && widthToggle) ? gridContainerMaxWidth : false;

    const gridBodyComponent = (
        <GridBodyRenderer
            gridData={gridData}
            columns={columns}
            isLoading={isLoading}
            onSortColumn={onSortColumn}
            order={order}
            orderBy={orderBy}
            legendConfig={legendConfig}
            dragAndDrop={dragAndDrop}
            handleDropRow={handleDropRow}
            massActions={massActions}
            reloadGridData={reloadGridData}
            onColumnResize={onColumnResize}
            massActionsProps={massActionsProps}
            setPopupUrl={setPopupUrl}
            setPopupConfig={setPopupConfig}
            height={props.height}
            setPopupType={setPopupType}
            setPopupMode={setPopupMode}
            setPopupComponent={setPopupComponent}
            setPopupComponentProps={setPopupComponentProps}
            t={t}
            multilines={multilines}
            defaultRowHeight={defaultRowHeight}
            columnsWidthPixels={columnsWidthPixels}
            setShowLoader={setShowLoader}
            gridName={gridName}
            visibleColumns={visibleColumns}
            rowExtraInfoConfig={rowExtraInfoConfig}
            gridReorderLoading={gridReorderLoading}
            form={props.form}
            setGridDataChanged={setGridDataChanged}
            uid={props.uid}
            filtersValue={filtersValue}
            customLegend={customLegend}
            autoRowHeight={autoRowHeight}
            gridEntityType={gridEntityType}
            gridView={gridView}
        />
    );

    const isActiveStickyFooterBtn = gridButtons && gridButtons.length && isStickyGridButtons;

    return (
        <Container className={'mi-container-grid'} maxWidth={gridContainerXs} component="main" disableGutters={true}>
            <Grid item container direction="column" data-test={`grid_${props.uid}`} ref={boxTableRef}>
                <Grid item data-test={`grid_${props.uid}_filters_container`} xs={12}>
                    {!(emptyData && true === hideEmpty) && !titleHidden && (
                        <TableFilters
                            uid={props.uid}
                            isEmptyState={emptyData}
                            filtersList={filtersList}
                            searchField={searchField}
                            filterValues={filtersValue}
                            setFiltersValue={handleFilterValueChange}
                            resetFilters={resetFilters}
                            resetFiltersButton={resetFiltersButton}
                            resetGridSettings={resetGridSettings}
                            resetColumns={resetColumns}
                            switchColumns={switchColumns}
                            gridTitle={gridTitle}
                            gridEntityType={gridEntityType}
                            gridDescription={props.gridDescription ?? gridDescription}
                            gridTranslate={t}
                            infoButton={infoButton}
                            gridName={gridName}
                            initVisibleFilters={visibleFilters}
                            visibleColumns={visibleColumns}
                            handleColumnSwitch={handleColumnSwitch}
                            fullwidthMode={fullwidthMode}
                            onFullwidthChange={onFullwidthChange}
                            widthToggle={widthToggle}
                            setPageIndex={setPageIndex}
                            removeHeaderIndent={props.removeHeaderIndent}
                            isLoading={isLoading || !readyToDraw}
                            onChangeView={onChangeView}
                            changeViewConfig={changeViewConfig}
                            gridView={gridView}
                            removeFilterValue={removeFilterValue}
                        />
                    )}
                </Grid>
                {props.injectedComponents && props.injectedComponents.length > 0 && props.form && (
                    <Grid item data-test={`grid_${props.uid}_injected_components`} sx={{ mb: 2 }}>
                        {props.injectedComponents.map((component, index) => {
                            const elementProps = prepareFormElementProps({
                                component: component,
                                form: props.form,
                            });
                            return (
                                <FormElement
                                    key={component.uid || component.name || index}
                                    {...elementProps}
                                    wrapWithContainer={false}
                                />
                            );
                        })}
                    </Grid>
                )}
                {!gridReorderLoading && (isLoading || !readyToDraw || !dataLoadingDone) ? (
                    <Grid item container justifyContent="center" data-test={`grid_${props.uid}_loading`}>
                        <GridLoading />
                    </Grid>
                ) : (
                    <Grid
                        item
                        data-test={`grid_${props.uid}_container`}
                        className={`grid-container ${
                            gridButtons && gridButtons.length > 0 && isStickyGridButtons
                                ? 'grid-container--sticky-mod'
                                : ''
                        }`}
                    >
                        {isError ? (
                            <RequestErrorMessage error={error} />
                        ) : gridData.length > 0 ? (
                            gridBodyComponent
                        ) : null}
                        {gridData.length === 0 && filtered && (
                            <NoSearchResult
                                emptyText={noSearchResult?.emptyText ? t(noSearchResult?.emptyText) : ''}
                                icon={noSearchResult?.icon}
                            />
                        )}
                        <Grid
                            item
                            container
                            className={`grid-footer ${
                                gridButtons && gridButtons.length > 0 ? 'grid-footer--buttons-exist' : ''
                            } ${isStickyGridButtons ? 'grid-footer--sticky-mod' : ''} ${
                                fullwidthMode === 'full' ? 'grid--full-mod' : ''
                            }`}
                            ref={refFooter}
                            sx={{
                                mt: emptyData && true === hideEmpty ? undefined : '6px',
                            }}
                        >
                            <Grid
                                item
                                container
                                className={`grid-footer-content-wrap`}
                                justifyContent="center"
                                sx={{
                                    right:
                                        conciergePanelVisible && isActiveStickyFooterBtn
                                            ? `${conciergeWidgetWidth}px`
                                            : 0,
                                    left:
                                        themeMenuSettings.main_navigation == 'left' && isActiveStickyFooterBtn
                                            ? '40px'
                                            : 0,
                                    width: conciergePanelVisible && isActiveStickyFooterBtn ? 'auto' : '100%',
                                }}
                            >
                                <Grid
                                    item
                                    container
                                    justifyContent="space-between"
                                    alignItems="center"
                                    className={'grid-footer-content'}
                                >
                                    <Grid item>
                                        <Grid container direction="row" spacing={2} alignItems="center">
                                            {gridButtons && gridButtons.length > 0 && (
                                                <TableButtons
                                                    uid={props.uid}
                                                    controllerUrl={controllerUrl}
                                                    gridButtons={gridButtons}
                                                    gridEntityType={gridEntityType}
                                                    massActionsProps={massActionsProps}
                                                    reloadGridData={reloadGridData}
                                                    setPopupUrl={setPopupUrl}
                                                    setPopupConfig={setPopupConfig}
                                                    setPopupType={setPopupType}
                                                    setPopupMode={setPopupMode}
                                                    setPopupRedirectAfter={setPopupRedirectAfter}
                                                    setFormPostData={setFormPostData}
                                                    setPopupGetMethod={setPopupGetMethod}
                                                    setPopupComponent={setPopupComponent}
                                                    setPopupComponentProps={setPopupComponentProps}
                                                    gridData={gridData}
                                                    massActionsConfig={massActions}
                                                    t={t}
                                                    setShowLoader={setShowLoader}
                                                    filtersValue={filtersValue}
                                                    form={props.form}
                                                />
                                            )}
                                            {gridData.length === 0 &&
                                            props.formGrid &&
                                            !filtered &&
                                            false === hideEmpty ? (
                                                <Grid item sx={{ flexShrink: 0 }}>
                                                    <Typography variant="body2">{emptyTitleText}</Typography>
                                                </Grid>
                                            ) : null}
                                        </Grid>
                                    </Grid>
                                    {pagerOptions &&
                                    pagerOptions.visible &&
                                    gridData.length > 0 &&
                                    totalCount > pagerOptions.pageSize ? (
                                        <MITablePagination
                                            uid={props.uid}
                                            pageSize={pagerOptions.pageSize}
                                            totalCount={totalCount}
                                            pageIndex={pageIndex}
                                            setPageIndex={setPageIndex}
                                        />
                                    ) : null}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                )}

                {gridPopup}
                {showLoader && <LoadingPlaceholder variant={'secondary'} relativeToParent />}
            </Grid>
        </Container>
    );
}
